import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/data/content";

export default function AllBoats() {
  const { t } = useApp();
  const [active, setActive] = useState(0);
  const types = t.boats.types;
  const cur = types[active];

  return (
    <section id="boats" className="relative py-28 lg:py-40 bg-ink text-white overflow-hidden" data-testid="boats-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight max-w-4xl leading-[1.03]">{t.boats.title}</h2>
          <p className="mt-6 max-w-xl text-slate leading-relaxed">{t.boats.sub}</p>
        </Reveal>

        <div className="mt-16 grid lg:grid-cols-[1fr_380px] gap-8 items-stretch">
          {/* big image */}
          <div className="relative rounded-2xl overflow-hidden aspect-[16/10] bg-gradient-to-br from-navy to-ink" data-testid="boats-image">
            <AnimatePresence mode="sync">
              <motion.img
                key={cur.id}
                src={IMAGES[cur.img]} alt={cur.t}
                initial={{ opacity: 0, scale: 1.04 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 w-full h-full object-contain"
              />
            </AnimatePresence>
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
            <div className="absolute top-5 left-5 font-mono text-[10px] tracking-[0.28em] uppercase text-white/80">{String(active + 1).padStart(2, "0")} / {String(types.length).padStart(2, "0")}</div>
            <AnimatePresence mode="wait">
              <motion.div key={cur.id} className="absolute bottom-6 left-6 right-6"
                initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
                <h3 className="font-display text-3xl md:text-5xl leading-none">{cur.t}</h3>
                <p className="mt-3 max-w-lg text-sm md:text-base text-slate">{cur.d}</p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* selector list */}
          <div className="flex flex-col divide-y divide-white/10 border-t border-white/10">
            {types.map((b, i) => (
              <button key={b.id} onClick={() => setActive(i)} data-testid={`boat-tab-${b.id}`}
                onMouseEnter={() => setActive(i)}
                className={`group flex items-center justify-between py-6 text-left transition-colors ${active === i ? "" : "opacity-55 hover:opacity-100"}`}>
                <span className="flex items-baseline gap-4">
                  <span className="font-mono text-xs text-brass">{String(i + 1).padStart(2, "0")}</span>
                  <span className={`font-display text-2xl md:text-3xl transition-colors ${active === i ? "text-brass" : "text-white"}`}>{b.t}</span>
                </span>
                <ArrowUpRight className={`w-5 h-5 transition-all ${active === i ? "text-brass translate-x-0" : "text-slate -translate-x-1 group-hover:translate-x-0"}`} />
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
